import { Dialog } from '@/components/ui/Dialog';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { useTermsContent } from '@/hooks/useTermsContent';
import { getApiErrorMessage } from '@/lib/utils/error.utils';

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
}

const TERMS_LOAD_ERROR_FALLBACK = 'We could not load the Terms & Conditions. Please try again.';

/**
 * Terms & Conditions dialog (MYN-33). Composes the shared `Dialog` primitive
 * (mnr-standards: "exactly one Dialog in ui/") and fetches the
 * currently-published terms via `useTermsContent` instead of the
 * hardcoded copy this component used to render. Only fetches once opened.
 */
export function TermsModal({ open, onClose }: TermsModalProps) {
  const { data, isLoading, isFetching, isError, error, refetch } = useTermsContent(open);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Terms & Conditions"
      size="xl"
    >
      <div className="max-h-[60vh] overflow-y-auto pr-1">
        {isLoading && (
          <div className="flex justify-center py-10">
            <LoadingSpinner label="Loading Terms & Conditions…" />
          </div>
        )}

        {isError && (
          <div role="alert" className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-danger">
              {getApiErrorMessage(error, TERMS_LOAD_ERROR_FALLBACK)}
            </p>
            <Button type="button" variant="outline" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? 'Retrying…' : 'Try again'}
            </Button>
          </div>
        )}

        {data && (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-subtext">{data.content}</p>
        )}
      </div>
    </Dialog>
  );
}
